/**
 * Simple Navigator - bypasses react-native-screens for iOS New Architecture compatibility
 * Uses basic React state to manage navigation instead of native navigation
 */

import React, {useState, createContext, useContext, useCallback, useMemo, useEffect, useRef} from 'react';
import {View, StyleSheet, Text, TouchableOpacity, Linking} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {useAuth} from '../contexts/AuthContext';
import {forwardGeocode, reverseGeocode} from '../services/geocoding';

// Screens - lazy import to avoid circular dependencies
const AnimatedSplashScreen = React.lazy(() => import('../screens/AnimatedSplashScreen'));
const SplashScreen = React.lazy(() => import('../screens/SplashScreen'));
const LoginScreen = React.lazy(() => import('../screens/LoginScreen'));
const SignupScreen = React.lazy(() => import('../screens/SignupScreen'));
const ResetPasswordScreen = React.lazy(() => import('../screens/ResetPasswordScreen'));
const MapScreen = React.lazy(() => import('../screens/MapScreen'));
const AccountManagementScreen = React.lazy(() => import('../screens/AccountManagementScreen'));
const SettingsScreen = React.lazy(() => import('../screens/SettingsScreen'));
const AccountSearchScreen = React.lazy(() => import('../screens/AccountSearchScreen'));
const ClosureLogsScreen = React.lazy(() => import('../screens/ClosureLogsScreen'));

// Import the shared navigation context
import {NavigationContext} from './navigationShim';

// This provides the same interface as React Navigation's useNavigation hook
export const MockNavigationProvider: React.FC<{
  children: React.ReactNode;
  navigate: (screen: string, params?: any) => void;
  goBack: () => void;
  currentScreen: string;
  screenStack: {name: string; params?: any}[];
}> = ({children, navigate, goBack, currentScreen, screenStack}) => {
  
  const navigationValue = useMemo(() => ({
    navigate,
    goBack,
    push: navigate,
    pop: goBack,
    popToTop: () => navigate('Main'),
    replace: navigate,
    reset: () => navigate('Main'),
    setOptions: () => {}, // No-op for now
    getState: () => ({
      routes: screenStack.map((s, i) => ({name: s.name, key: `${s.name}-${i}`, params: s.params})),
      index: screenStack.length - 1,
    }),
    addListener: (event: string, callback: () => void) => {
      // Simple focus/blur simulation - just return unsubscribe function
      if (event === 'focus') {
        // Call immediately since we're "focused"
        setTimeout(callback, 0);
      }
      return () => {};
    },
    isFocused: () => true,
    canGoBack: () => screenStack.length > 1,
    getParent: () => null,
    setParams: () => {},
    dispatch: () => {},
  }), [navigate, goBack, currentScreen, screenStack]);
  
  return (
    <NavigationContext.Provider value={navigationValue}>
      {children}
    </NavigationContext.Provider>
  );
};

const SimpleNavigator: React.FC = () => {
  const {user, loading} = useAuth();
  const [screenStack, setScreenStack] = useState<{name: string; params?: any}[]>([]);
  const prevUserRef = useRef<typeof user>(undefined);
  
  // Reset navigation stack when auth state changes (login/logout)
  useEffect(() => {
    // Skip on initial mount or during loading
    if (loading) return;
    
    const wasLoggedIn = prevUserRef.current !== null && prevUserRef.current !== undefined;
    const isLoggedIn = user !== null && user !== undefined;
    
    // Only reset if auth state actually changed
    if (wasLoggedIn !== isLoggedIn) {
      console.log('🔄 Auth state changed, resetting navigation stack');
      setScreenStack([]); // Clear stack to use initial screen
    }
    
    prevUserRef.current = user;
  }, [user, loading]);
  
  // Track if animated splash has been shown
  const [hasShownAnimatedSplash, setHasShownAnimatedSplash] = React.useState(false);
  
  // Check for deep links before deciding which screen to show
  const [deepLinkChecked, setDeepLinkChecked] = React.useState(false);
  const [deepLinkScreen, setDeepLinkScreen] = React.useState<string | null>(null);
  
  useEffect(() => {
    const handleSubscriptionLink = (url: string) => {
      if (url && url.startsWith('respondermap://subscription-success')) {
        setDeepLinkScreen('Login');
        setHasShownAnimatedSplash(true);
        setScreenStack([{name: 'Login'}]);
      }
    };

    // Finalize a navigation destination and hand off to MapScreen via AsyncStorage
    const commitNavigationDestination = async (
      lat: number,
      lng: number,
      label: string | null,
      timestamp?: number,
    ) => {
      console.log('🧭 Navigation intent received — centering map on', lat, lng, label ? `(${label})` : '');
      const payload = JSON.stringify({
        latitude: lat,
        longitude: lng,
        label: label || undefined,
        timestamp: timestamp ?? Date.now(),
      });
      await AsyncStorage.setItem('pendingClosureLocation', payload);
      await AsyncStorage.setItem('navigationDestination', payload);
      setHasShownAnimatedSplash(true);
      setScreenStack([{name: 'Main'}]);
    };

    // Decode a URL-encoded parameter value safely
    const decodeParam = (value: string | null | undefined): string | null => {
      if (!value) return null;
      try {
        return decodeURIComponent(value.replace(/\+/g, ' '));
      } catch {
        return value;
      }
    };

    // Extract a single query-string parameter from a URL by name
    const extractParam = (url: string, paramName: string): string | null => {
      const regex = new RegExp(`[?&]${paramName}=([^&]+)`, 'i');
      const match = url.match(regex);
      return match ? decodeParam(match[1]) : null;
    };

    const handleNavigationIntent = async (url: string) => {
      if (!url) return false;

      let lat: number | null = null;
      let lng: number | null = null;
      let label: string | null = null;
      let addressFallback: string | null = null;

      if (url.startsWith('respondermap://navigate')) {
        // respondermap://navigate?destination=LAT,LNG
        // respondermap://navigate?destination=LAT,LNG&label=Encoded+Address
        // respondermap://navigate?address=Encoded+Address
        const destination = extractParam(url, 'destination');
        if (destination) {
          const destMatch = destination.match(/^(-?\d+\.?\d*),(-?\d+\.?\d*)$/);
          if (destMatch) {
            lat = parseFloat(destMatch[1]);
            lng = parseFloat(destMatch[2]);
          }
        }
        label = extractParam(url, 'label');
        if (lat === null) {
          addressFallback = extractParam(url, 'address');
        }
      } else if (url.startsWith('geo:')) {
        // geo:lat,lng  or  geo:lat,lng?q=lat,lng(label)  or  geo:0,0?q=address-text
        const withoutScheme = url.substring(4);
        const coordMatch = withoutScheme.match(/^(-?\d+\.?\d*),(-?\d+\.?\d*)/);
        if (coordMatch && parseFloat(coordMatch[1]) !== 0) {
          lat = parseFloat(coordMatch[1]);
          lng = parseFloat(coordMatch[2]);
        }
        if (lat === null) {
          const qCoordMatch = withoutScheme.match(/[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
          if (qCoordMatch) {
            lat = parseFloat(qCoordMatch[1]);
            lng = parseFloat(qCoordMatch[2]);
          }
        }
        if (lat === null) {
          // Address text fallback: geo:0,0?q=123+Main+St,+Anytown
          const qTextMatch = withoutScheme.match(/[?&]q=([^&]+)/);
          if (qTextMatch) {
            addressFallback = decodeParam(qTextMatch[1]);
          }
        }
      } else if (url.startsWith('google.navigation:')) {
        // google.navigation:q=lat,lng  or  google.navigation:q=address
        const qCoordMatch = url.match(/[?&:]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
        if (qCoordMatch) {
          lat = parseFloat(qCoordMatch[1]);
          lng = parseFloat(qCoordMatch[2]);
        } else {
          const qTextMatch = url.match(/[?&:]q=([^&]+)/);
          if (qTextMatch) {
            addressFallback = decodeParam(qTextMatch[1]);
          }
        }
      }

      // If we have coordinates directly, commit and return
      if (lat !== null && lng !== null && !isNaN(lat) && !isNaN(lng)) {
        // Center the map on the destination immediately — don't wait for
        // reverseGeocode to finish. Writing pendingClosureLocation now lets
        // MapScreen seed/animate to the destination on its next poll tick,
        // avoiding a visible "bounce to My Location" while geocoding retries.
        // We capture one timestamp and reuse it in commitNavigationDestination
        // below so MapScreen's locationKey dedup skips the second animation.
        const navTimestamp = Date.now();
        try {
          await AsyncStorage.setItem(
            'pendingClosureLocation',
            JSON.stringify({
              latitude: lat,
              longitude: lng,
              timestamp: navTimestamp,
            }),
          );
          setHasShownAnimatedSplash(true);
          setScreenStack([{name: 'Main'}]);
        } catch (e) {
          console.warn('🧭 Could not write early pendingClosureLocation:', e);
        }

        // If no label was provided (e.g. Active 911 sends coords only),
        // reverse-geocode so the marker callout shows the dispatched address
        // instead of the generic "Navigation Destination" placeholder.
        let finalLabel = label;
        if (!finalLabel) {
          try {
            const geocoded = await reverseGeocode(lat, lng);
            // reverseGeocode always resolves; skip the coordinate-only fallback
            // ("40.7, -74.0" has no letters) so we don't show raw numbers.
            if (geocoded?.fullAddress && /[a-zA-Z]/.test(geocoded.fullAddress)) {
              finalLabel = geocoded.fullAddress;
            }
          } catch (e) {
            console.warn('🧭 Reverse geocode failed for navigation intent:', e);
          }
        }
        await commitNavigationDestination(lat, lng, finalLabel, navTimestamp);
        return true;
      }

      // No coordinates — try geocoding the address string
      if (addressFallback) {
        const geocoded = await forwardGeocode(addressFallback);
        if (geocoded) {
          await commitNavigationDestination(
            geocoded.latitude,
            geocoded.longitude,
            label || geocoded.formattedAddress,
          );
          return true;
        }
        console.warn('🧭 Could not geocode address from navigation intent:', addressFallback);
      }

      return false;
    };

    const handleDeepLink = async (url: string) => {
      if (!url) return;
      if (url.startsWith('respondermap://navigate')) {
        await handleNavigationIntent(url);
      } else if (url.startsWith('respondermap://')) {
        handleSubscriptionLink(url);
      } else if (url.startsWith('geo:') || url.startsWith('google.navigation:')) {
        await handleNavigationIntent(url);
      }
    };

    // Cold start: app opened from deep link
    Linking.getInitialURL().then((url) => {
      if (url) handleDeepLink(url);
    }).catch(() => {}).finally(() => {
      setDeepLinkChecked(true);
    });

    // Warm start: app already running, receives deep link
    const sub = Linking.addEventListener('url', (event) => {
      handleDeepLink(event.url);
    });

    return () => sub.remove();
  }, []);
  
  // Determine initial screen based on auth and deep link
  const initialScreen = user 
    ? 'Main' 
    : deepLinkScreen ? deepLinkScreen
    : (!hasShownAnimatedSplash ? 'AnimatedSplash' : 'Splash');
  const effectiveStack = screenStack.length > 0 ? screenStack : [{name: initialScreen}];
  const currentScreen = effectiveStack[effectiveStack.length - 1];
  
  const navigate = useCallback((screen: string, params?: any) => {
    console.log('📍 Navigate to:', screen, params);
    setScreenStack(prev => [...prev, {name: screen, params}]);
  }, []);
  
  const goBack = useCallback(() => {
    console.log('📍 Go back');
    setScreenStack(prev => prev.length > 1 ? prev.slice(0, -1) : prev);
  }, []);
  
  if (loading || !deepLinkChecked) {
    return (
      <View style={styles.loadingContainer}>
        <Text style={styles.loadingText}>Loading...</Text>
        <Text style={styles.loadingSubtext}>Connecting to Firebase...</Text>
      </View>
    );
  }
  
  // Render the current screen with Suspense for lazy loading
  const renderScreen = () => {
    const {name, params} = currentScreen;
    const route = {params: params || {}, name, key: `${name}-${Date.now()}`};
    
    return (
      <React.Suspense fallback={<View style={styles.loadingContainer}><Text style={styles.loadingText}>Loading screen...</Text></View>}>
        {(() => {
          switch (name) {
            case 'AnimatedSplash':
              return (
                <AnimatedSplashScreen
                  navigation={{
                    navigate: (screen: string) => {
                      setHasShownAnimatedSplash(true);
                      navigate(screen);
                    },
                  }}
                />
              );
            case 'Splash':
              return <SplashScreen navigation={{navigate, goBack}} />;
            case 'Login':
              return <LoginScreen navigation={{navigate, goBack}} route={route} />;
            case 'Signup':
              return <SignupScreen navigation={{navigate, goBack}} route={route} />;
            case 'ResetPassword':
              return <ResetPasswordScreen navigation={{navigate, goBack}} route={route} />;
            case 'Main':
              return <MainTabView navigate={navigate} goBack={goBack} screenStack={effectiveStack} initialTab={params?.activeTab} />;
            case 'AccountSearch':
              return <AccountSearchScreen navigation={{navigate, goBack}} />;
            case 'ClosureLogs':
              return <ClosureLogsScreen navigation={{navigate, goBack}} route={route} />;
            default:
              return <SplashScreen navigation={{navigate, goBack}} />;
          }
        })()}
      </React.Suspense>
    );
  };
  
  return (
    <MockNavigationProvider 
      navigate={navigate} 
      goBack={goBack} 
      currentScreen={currentScreen.name}
      screenStack={effectiveStack}
    >
      <View style={styles.container}>
        {renderScreen()}
      </View>
    </MockNavigationProvider>
  );
};

// Simple tab view for main screens
const MainTabView: React.FC<{navigate: (s: string, p?: any) => void; goBack: () => void; screenStack: any[]; initialTab?: string}> = ({navigate, goBack, screenStack, initialTab}) => {
  const [activeTab, setActiveTab] = useState(initialTab || 'Map');
  const insets = useSafeAreaInsets();
  
  // Debug: Log the safe area insets
  console.log('📐 Tab Bar Safe Area Insets:', insets);
  
  // Update activeTab when initialTab prop changes (e.g., when navigating to Main with a specific tab)
  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  // Watch AsyncStorage for pending notification closure location.
  // When data arrives while the user is on Account/Settings, switch to Map tab.
  useEffect(() => {
    let active = true;
    const checkPending = async () => {
      if (!active) return;
      try {
        const val = await AsyncStorage.getItem('pendingClosureLocation');
        if (val && active) {
          setActiveTab(prev => {
            if (prev !== 'Map') {
              console.log('📍 Pending closure detected — switching to Map tab');
              return 'Map';
            }
            return prev;
          });
        }
      } catch {}
    };
    const interval = setInterval(checkPending, 500);
    checkPending();
    return () => { active = false; clearInterval(interval); };
  }, []);
  
  const renderTab = () => {
    const route = {params: {}, name: activeTab, key: `${activeTab}-tab`};
    const navigation = {navigate, goBack};
    
    return (
      <React.Suspense fallback={<View style={styles.loadingContainer}><Text style={styles.loadingText}>Loading...</Text></View>}>
        <MockNavigationProvider navigate={navigate} goBack={goBack} currentScreen={activeTab} screenStack={screenStack}>
          {(() => {
            switch (activeTab) {
              case 'Map':
                return <MapScreen />;
              case 'Account':
                return <AccountManagementScreen navigation={navigation} route={route} />;
              case 'Settings':
                return <SettingsScreen navigation={navigation} route={route} />;
              default:
                return <MapScreen />;
            }
          })()}
        </MockNavigationProvider>
      </React.Suspense>
    );
  };
  
  return (
    <View style={styles.container}>
      <View style={styles.content}>
        {renderTab()}
      </View>
      
      {/* Tab bar - with safe area padding for Home Indicator */}
      <View style={[styles.tabBar, {paddingBottom: insets.bottom}]}>
        <TabButton title="📍 Map" active={activeTab === 'Map'} onPress={() => setActiveTab('Map')} />
        <TabButton title="👤 Account" active={activeTab === 'Account'} onPress={() => setActiveTab('Account')} />
        <TabButton title="⚙️ Settings" active={activeTab === 'Settings'} onPress={() => setActiveTab('Settings')} />
      </View>
    </View>
  );
};

const TabButton: React.FC<{title: string; active: boolean; onPress: () => void}> = ({title, active, onPress}) => {
  return (
    <TouchableOpacity style={[styles.tabButton, active && styles.activeTab]} onPress={onPress}>
      <Text style={[styles.tabText, active && styles.activeTabText]}>{title}</Text>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#424242',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#424242',
  },
  loadingText: {
    color: '#424242', // Match background color to make text invisible
    fontSize: 24,
    fontWeight: 'bold',
  },
  loadingSubtext: {
    color: '#424242', // Match background color to make text invisible
    fontSize: 14,
    marginTop: 10,
  },
  content: {
    flex: 1,
  },
  tabBar: {
    flexDirection: 'row',
    backgroundColor: '#424242',
    borderTopWidth: 1,
    borderTopColor: '#333',
  },
  tabButton: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
  },
  activeTab: {
    borderTopWidth: 2,
    borderTopColor: '#2196F3',
  },
  tabText: {
    color: '#888',
    fontSize: 12,
  },
  activeTabText: {
    color: '#2196F3',
  },
});

export default SimpleNavigator;

